<script setup lang="ts">
import { ref, watch, watchEffect, nextTick, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons-vue'
import AppHeader from './AppHeader.vue'
import AppSidebar from './AppSidebar.vue'
import AppBreadcrumb from './AppBreadcrumb.vue'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { usePermissionStore } from '@/stores/permission'
import {
  applyDeclaredButtonPermissionMask,
  watchDeclaredButtonPermissionMask,
} from '@/utils/buttonPermissionMask'

const route = useRoute()
const permissionStore = usePermissionStore()
const { loaded, loadFailed, codeSet } = storeToRefs(permissionStore)
const { isMobile, isTablet } = useBreakpoint()

const sidebarCollapsed = ref(false)
const mobileMenuOpen = ref(false)
const contentRef = ref<HTMLElement | null>(null)
let stopMaskObserver: (() => void) | null = null

function toggleSidebar() {
  if (isMobile.value) {
    mobileMenuOpen.value = !mobileMenuOpen.value
  } else {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

watch(
  () => route.fullPath,
  () => {
    closeMobileMenu()
  },
)

watch(isMobile, (mobile) => {
  if (mobile) {
    mobileMenuOpen.value = false
    document.body.style.overflow = ''
  }
})

watch(mobileMenuOpen, (open) => {
  if (!isMobile.value) return
  document.body.style.overflow = open ? 'hidden' : ''
})

watch(
  isTablet,
  (tablet) => {
    if (tablet && !isMobile.value) {
      sidebarCollapsed.value = true
    }
  },
  { immediate: true },
)

/** 路由切换后整页 DOM 重建一次即可；权限码变化时再全量重扫。Observer 负责后续异步插入节点。 */
watch(
  () => route.fullPath,
  () => {
    void nextTick(() => {
      applyDeclaredButtonPermissionMask(contentRef.value)
      if (!stopMaskObserver && contentRef.value) {
        stopMaskObserver = watchDeclaredButtonPermissionMask(contentRef.value)
      }
    })
  },
  { immediate: true },
)

watchEffect(() => {
  void loaded.value
  void loadFailed.value
  void codeSet.value.size
  void nextTick(() => {
    applyDeclaredButtonPermissionMask(contentRef.value)
  })
})

onUnmounted(() => {
  stopMaskObserver?.()
  stopMaskObserver = null
  document.body.style.overflow = ''
})
</script>

<template>
  <div class="main-layout">
    <AppHeader />
    <div class="layout-body">
      <Transition name="sidebar-backdrop-fade">
        <div
          v-if="isMobile && mobileMenuOpen"
          class="sidebar-backdrop"
          aria-hidden="true"
          @click="closeMobileMenu"
        />
      </Transition>
      <AppSidebar
        :collapsed="sidebarCollapsed"
        :mobile="isMobile"
        :mobile-open="mobileMenuOpen"
        @close="closeMobileMenu"
      />
      <main class="layout-main">
        <div class="main-content-header">
          <a class="sidebar-trigger" aria-label="切换侧边栏" @click="toggleSidebar">
            <MenuUnfoldOutlined v-if="isMobile ? !mobileMenuOpen : sidebarCollapsed" />
            <MenuFoldOutlined v-else />
          </a>
          <div class="main-breadcrumb-wrap">
            <AppBreadcrumb />
          </div>
        </div>
        <div ref="contentRef" class="main-content-wrapper">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.main-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  overflow: hidden;
}

.layout-body {
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.sidebar-backdrop {
  position: fixed;
  inset: var(--layout-header-height) 0 0 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  cursor: pointer;
}

.sidebar-backdrop-fade-enter-active,
.sidebar-backdrop-fade-leave-active {
  transition: opacity 0.2s ease;
}

.sidebar-backdrop-fade-enter-from,
.sidebar-backdrop-fade-leave-to {
  opacity: 0;
}

.layout-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: linear-gradient(180deg, var(--layout-main-bg) 0%, var(--surface-sunken) 100%);
  transition: background 0.25s ease;
}

.main-content-header {
  flex-shrink: 0;
  width: 100%;
  min-height: 52px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  padding: 10px var(--space-lg);
  box-sizing: border-box;
  background: var(--layout-content-header-bg);
  border-bottom: 1px solid var(--layout-content-header-border);
  box-shadow: var(--shadow-sm);
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}

.sidebar-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: var(--layout-trigger-color);
  font-size: 18px;
  border-radius: 8px;
  cursor: pointer;
  transition:
    color 0.2s,
    background 0.2s;
  flex-shrink: 0;
}

.sidebar-trigger:hover {
  color: var(--app-primary);
  background: var(--app-primary-soft);
}

.main-breadcrumb-wrap {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.main-content-wrapper {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: var(--space-lg);
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
}

html[data-theme='dark'] .main-content-wrapper {
  background: transparent;
}

@media (max-width: 991px) {
  .main-content-header {
    padding: 8px var(--space-md);
    min-height: 48px;
  }

  .main-content-wrapper {
    padding: var(--space-md);
  }
}

@media (max-width: 575px) {
  .main-content-header {
    padding: 8px 12px;
    gap: 8px;
  }

  .main-content-wrapper {
    padding: 12px;
  }
}
</style>
